import React, { forwardRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { ReactComponent as AutoAppLogoClean } from '../../assets/AutoappLogoClean.svg';
import { InlineAlert } from '../alerts';

const LoginForm = forwardRef(
  (
    {
      fieldErrors = {},
      email,
      password,
      showPassword,
      result,
      resetResult,
      isFormValid,
      loading,
      loadingMessage,
      onEmailChange,
      onPasswordChange,
      onShowPasswordClick,
      onForgotPassword,
      onSubmit,
      isDark,
      colors,
    },
    ref
  ) => {
    const titleColor = isDark ? '#fff' : colors.textDark;
    const subtitleColor = isDark
      ? 'rgba(255, 255, 255, 0.6)'
      : colors.textSecondary;
    const fieldTextColor = isDark ? '#fff' : colors.textDark;
    const fieldLabelColor = isDark
      ? 'rgba(255, 255, 255, 0.6)'
      : colors.textSecondary;
    const fieldBorderColor = isDark
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.38)';
    const fieldHoverBorderColor = isDark
      ? 'rgba(255, 255, 255, 0.4)'
      : 'rgba(0, 0, 0, 0.6)';
    const fieldFocusBorderColor = isDark ? '#fff' : colors.primary;
    const fieldIconColor = isDark
      ? 'rgba(255, 255, 255, 0.6)'
      : colors.textSecondary;

    return (
      <Box
        ref={ref}
        sx={{
          p: { xs: 2, sm: 4 },
          width: '100%',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              mb: 2,
              p: 0,
            }}
          >
            <AutoAppLogoClean alt="AutoApp Logo" height="60px" width="60px" />
          </Box>
          <Typography
            component="h1"
            variant="h4"
            sx={{
              fontWeight: 700,
              color: titleColor,
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            AutoApp
          </Typography>
          <Typography
            component="h2"
            variant="h6"
            sx={{ color: subtitleColor, fontWeight: 500, mb: 4 }}
          >
            Iniciá sesión en tu cuenta
          </Typography>

          <Box
            component="form"
            onSubmit={onSubmit}
            noValidate
            sx={{ width: '100%', maxWidth: '400px' }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={onEmailChange}
              error={Boolean(fieldErrors?.email)}
              helperText={fieldErrors?.email || ''}
              InputLabelProps={{
                style: { color: fieldLabelColor },
              }}
              InputProps={{
                style: { color: fieldTextColor },
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: fieldIconColor }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    borderColor: fieldBorderColor,
                  },
                  '&:hover fieldset': {
                    borderColor: fieldHoverBorderColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: fieldFocusBorderColor,
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336 !important',
                  },
                  '&.Mui-error:hover fieldset': {
                    borderColor: '#f44336 !important',
                  },
                  '&.Mui-error.Mui-focused fieldset': {
                    borderColor: '#f44336 !important',
                  },
                },
                '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                  WebkitBoxShadow: `0 0 0 1000px ${isDark ? '#111' : colors.inputBackground} inset`,
                  WebkitTextFillColor: fieldTextColor,
                  caretColor: fieldTextColor,
                  transition: 'background-color 5000s ease-in-out 0s',
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={onPasswordChange}
              error={Boolean(fieldErrors?.password)}
              helperText={fieldErrors?.password || ''}
              InputLabelProps={{
                style: { color: fieldLabelColor },
              }}
              InputProps={{
                style: { color: fieldTextColor },
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: fieldIconColor }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={onShowPasswordClick} edge="end" aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"} sx={{ color: fieldIconColor }}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    borderColor: fieldBorderColor,
                  },
                  '&:hover fieldset': {
                    borderColor: fieldHoverBorderColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: fieldFocusBorderColor,
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336 !important',
                  },
                  '&.Mui-error:hover fieldset': {
                    borderColor: '#f44336 !important',
                  },
                  '&.Mui-error.Mui-focused fieldset': {
                    borderColor: '#f44336 !important',
                  },
                },
                '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                  WebkitBoxShadow: `0 0 0 1000px ${isDark ? '#111' : colors.inputBackground} inset`,
                  WebkitTextFillColor: fieldTextColor,
                  caretColor: fieldTextColor,
                  transition: 'background-color 5000s ease-in-out 0s',
                },
              }}
            />
            {!loading && (
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={!isFormValid}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  background: isFormValid
                    ? (isDark ? '#fff' : colors.primary)
                    : (isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 124, 162, 0.18)'),
                  color: isFormValid
                    ? (isDark ? '#000' : '#fff')
                    : (isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 124, 162, 0.65)'),
                  boxShadow: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&.Mui-disabled': {
                    background: isDark
                      ? 'rgba(255, 255, 255, 0.15)'
                      : 'rgba(0, 124, 162, 0.18)',
                    color: isDark
                      ? 'rgba(255, 255, 255, 0.5)'
                      : 'rgba(0, 124, 162, 0.65)',
                  },
                  '&:hover': isFormValid
                    ? {
                        background: isDark ? '#d4d4d4' : colors.primaryDark,
                        boxShadow: isDark
                          ? '0 8px 24px rgba(255, 255, 255, 0.2)'
                          : '0 8px 24px rgba(0, 124, 162, 0.22)',
                        transform: 'translateY(-2px)',
                        color: isDark ? '#000' : '#fff',
                      }
                    : {},
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                }}
              >
                Iniciar Sesión
              </Button>
            )}
            {loading && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 3,
                  mb: 2,
                  py: 2,
                }}
              >
                <CircularProgress
                  size={32}
                  thickness={4}
                  sx={{ color: isDark ? '#fff' : colors.primary }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    ml: 2,
                    color: isDark ? 'rgba(255,255,255,0.7)' : colors.textSecondary,
                    fontWeight: 500,
                  }}
                >
                  {loadingMessage || 'Iniciando sesión...'}
                </Typography>
              </Box>
            )}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.7)' : colors.textSecondary,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  '&:hover': {
                    color: isDark ? '#fff' : colors.textDark,
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,124,162,0.06)',
                  },
                }}
                onClick={onForgotPassword}
              >
                ¿Olvidaste tu contraseña?
              </Button>
            </Box>
            <InlineAlert
              result={resetResult}
              severity={resetResult?.success === 'yes' ? 'success' : 'warning'}
              sx={{
                display: resetResult ? 'flex' : 'none',
              }}
            >
              {resetResult?.message || resetResult?.error}
            </InlineAlert>
          </Box>
        </Box>
      </Box>
    );
  }
);

LoginForm.displayName = 'LoginForm';

export default LoginForm;
